import type { Session, User } from '@supabase/supabase-js';
import { QueryClient, queryOptions } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';

import { supabaseClient } from '../supabase';
import { fetchBackend } from './apiClient';

export const signUp = async (
  email: string,
  password: string,
): Promise<void> => {
  const response = await fetchBackend(
    `/auth/sign-up`,
    {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    },
    false,
  );

  if (!response.ok) {
    throw new Error(`Error: ${response.status}`);
  }

  return;
};

export const signUpAndInviteUserToProject = async (
  email: string,
): Promise<void> => {
  const response = await fetchBackend(`/auth/invite-user`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    throw new Error(`Error: ${response.status}`);
  }

  return;
};

// We use react-query to avoid calling supabase every time a user hovers over a link
export const userQueryOptions = queryOptions({
  queryKey: ['supabase', 'user'],
  queryFn: async () => supabaseClient.auth.getUser(),
});

export const preLoadUserAndSession = async ({
  context,
}: {
  context: { queryClient: QueryClient };
}): Promise<{
  user: User;
  session: Session;
}> => {
  const [
    {
      error: sessionError,
      data: { session },
    },
    {
      error: userError,
      data: { user },
    },
  ] = await Promise.all([
    supabaseClient.auth.getSession(),
    context.queryClient.ensureQueryData(userQueryOptions),
  ]);

  if (sessionError || userError) {
    console.error(sessionError, userError);
    context.queryClient.clear();

    throw redirect({ to: '/sign-in' });
  }

  if (session === null || user === null) {
    context.queryClient.clear();

    throw redirect({ to: '/sign-in' });
  }

  return { user, session };
};
